import { defineStore } from 'pinia';
import { computed } from 'vue';
import { useSettingStore } from '../../setting/stores/settingStore';
import { type StorageArea } from '../domain/storageArea';
import { StorageAreaType } from '../domain/storageAreaType';
import { useEntityStore } from '../../common/composables/entityStore';

export const useStorageAreaStore = defineStore('storageAreas', () => {
  const { add, remove, merge, getById, getOrThrow, clear, list, changed } =
    useEntityStore<StorageArea>({
      eventName: 'storage-area-changed',
      getEntityId: (e) => e.id,
      updateEntity(dest, src) {
        dest.active = src.active;
        dest.coilStack = src.coilStack;
        dest.logYardData = src.logYardData;
        dest.bulkStorageData = src.bulkStorageData;
        dest.packageStorageData = src.packageStorageData;
        dest.productionLineData = src.productionLineData;
        dest.packagePlaceData = src.packagePlaceData;
        dest.name = src.name;
        dest.note = src.note;
        dest.status = src.status;
        dest.temporary = src.temporary;
        dest.type = src.type;
        dest.updatedAtUtc = src.updatedAtUtc;
        dest.version = src.version;
        dest.orderName = src.orderName;
        dest.importantNote = src.importantNote;
        dest.displayText = src.displayText;
      },
      hasChanged(existing, incoming) {
        return existing.version !== incoming.version
      },
    });

  const settingStore = useSettingStore();

  function generateDefaultName() {
    const nameFormat = settingStore.setting?.sharedClient.storageArea.nameFormat ?? '';

    let maxNumber = 0;
    const regex = new RegExp(nameFormat.replace('{#}', '(\\d+)'));

    for (const storageArea of list.value) {
      const match = storageArea.name.match(regex);
      if (match) {
        const number = parseInt(match[1] ?? '0');
        if (number > maxNumber) {
          maxNumber = number;
        }
      }
    }

    const newName = nameFormat.replace('{#}', (maxNumber + 1).toString());
    return newName;
  }

  return {
    getById,
    generateDefaultName,
    add,
    remove,
    merge,
    getOrThrow,
    clear,
    storageAreas: computed(() => list.value),
    sawLineIntakes: computed(() =>
      list.value.filter((s) => s.type === StorageAreaType.SawLineIntake),
    ),
    logSorterBins: computed(() =>
      list.value.filter((s) => s.type === StorageAreaType.LogSorterBin),
    ),
    changed,
  };
});
